import React, { PropsWithChildren, ReactElement, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { Column, CellProps } from 'react-table';
import styled from 'styled-components';
import API from 'core/Api';
import Form from 'components/form/Form';
import ReactTableWrapper from 'components/table/ReactTableWrapper';
import ActionCell from 'components/actions/ActionCell';
import { ISelectOptionProps } from 'components/form/types';
import { useModal } from 'store/modal';
import { createErrorFromResponse } from 'core/Error';
import { buildClientEntity } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import { usePermissionsContext } from 'store/permissions';
import { PermissionKeys } from 'store/types';
import { useTranslation } from 'react-i18next';
import setServerErrors from 'utils/helpers/formErrors';
import { IMultiActionsByCategory, ITemplateActions } from '../types';
import DefaultActions from './DefaultActions';

interface IProps<T extends Record<string, unknown>> {
  data: IMultiActionsByCategory;
}

function FormTable<T extends Record<string, unknown>>({
  data,
}: PropsWithChildren<IProps<ITemplateActions>>): ReactElement {
  const { t } = useTranslation();
  const { id: categoryId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { client } = useActiveClientContext();
  const { checkPermission } = usePermissionsContext();
  const { showModal } = useModal();

  const hasPermission = checkPermission(PermissionKeys.CAN_MANAGE_DOCUMENT_PERMISSIONS);

  const {
    approve: approveDefaults,
    sign: signDefaults,
    approve_options: approveOptions,
    sign_options: signOptions,
    table_data: tableData,
  } = data;

  // Default values
  const buildDefaultValues = () => {
    const obj: any = {};
    approveDefaults.forEach((def, index) => {
      obj[`default-approve-${index + 1}`] = def;
    });
    signDefaults.forEach((def, index) => {
      obj[`default-sign-${index + 1}`] = def;
    });
    tableData.items?.forEach((item: ITemplateActions) => {
      item.approve.forEach((appr: ISelectOptionProps, index: number) => {
        obj[`approve-${item.template_id}-${index + 1}`] = appr;
      });

      item.sign.forEach((sgn: ISelectOptionProps, index: number) => {
        obj[`sign-${item.template_id}-${index + 1}`] = sgn;
      });
    });
    return obj;
  };

  const defaultValues = buildDefaultValues();

  // Table columns
  const columns: Array<Column<ITemplateActions>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('structure.multiActions.number') || '',
      accessor: 'template_id',
      width: '6%',
    },
    {
      Header: t('structure.multiActions.docName') || '',
      accessor: 'template_name',
      disableSortBy: true,
      width: '40%',
    },
    {
      Header: t('structure.multiActions.type') || '',
      accessor: 'template_type',
      disableSortBy: true,
      width: '12%',
    },
    {
      Header: t('structure.multiActions.approvals') || '',
      accessor: 'approve',
      disableSortBy: true,
      width: '21%',
      maxWidth: 200,
      minWidth: 200,
      Cell: ({ row: { index, original }, rows, column: { id }, updateCellData }: CellProps<ITemplateActions>) => {
        if (!original.has_actions)
          return <NoActionHolder>{t('structure.multiActions.approvalNotRequired')}</NoActionHolder>;
        const maxPositionsAvailable: number = approveOptions.reduce((optionCount, group) => {
          const optionsLength = group?.options?.length || 0;
          return optionCount + optionsLength;
        }, 0);
        return (
          <ActionCell
            name={`approve-${original.template_id}`}
            options={approveOptions}
            selected={rows.find(row => row.values.template_id === original.template_id)?.values.approve || []}
            maxPositionsAvailable={maxPositionsAvailable}
            methods={methods}
            updateData={actionData => {
              updateCellData(index, id, actionData);
            }}
            hasPermission={hasPermission}
          />
        );
      },
    },
    {
      Header: t('structure.multiActions.signatures') || '',
      accessor: 'sign',
      disableSortBy: true,
      width: '21%',
      maxWidth: 200,
      minWidth: 200,
      Cell: ({ row: { index, original }, rows, column: { id }, updateCellData }: CellProps<ITemplateActions>) => {
        if (!original.has_actions)
          return <NoActionHolder>{t('structure.multiActions.signNotRequired')}</NoActionHolder>;
        const maxPositionsAvailable: number = signOptions.reduce((optionCount, group) => {
          const optionsLength = group?.options?.length || 0;
          return optionCount + optionsLength;
        }, 0);

        return (
          <ActionCell
            name={`sign-${original.template_id}`}
            options={signOptions}
            maxPositionsAvailable={maxPositionsAvailable}
            selected={rows.find(row => row.values.template_id === original.template_id)?.values.sign || []}
            methods={methods}
            updateData={actionData => {
              updateCellData(index, id, actionData);
            }}
            hasPermission={hasPermission}
          />
        );
      },
    },
  ];

  // Form submit
  const onSubmit = (submitData: any) => {
    // Set all templates default empty action arrays
    // that should be sent to API even if there are no positions selected
    const dataObj = {
      category_id: categoryId,
      approve: [],
      sign: [],
      templates: tableData.items.reduce((obj, curr) => {
        return {
          ...obj,
          [`approve_${curr.template_id}`]: [],
          [`sign_${curr.template_id}`]: [],
        };
      }, {}),
    };

    // Default actions
    const defaultDataArr = Object.keys(submitData)
      .filter(key => key.includes('default'))
      .map(key => ({
        field: key,
        value: submitData[key].value,
      }));

    const defaultActions = defaultDataArr.reduce((obj: { [key: string]: any[] }, curr) => {
      const params = curr.field.split('-');
      const objKey = params[1];

      return {
        ...obj,
        [objKey]: [...(obj[objKey] || []), curr.value],
      };
    }, {});

    // Templates actions
    const dataArr = Object.keys(submitData)
      .filter(key => !key.includes('default'))
      .map(key => ({
        field: key,
        value: submitData[key].value,
      }));

    const templates = dataArr.reduce((obj: { [key: string]: any[] }, curr) => {
      const params = curr.field.split('-');
      const objKey = `${params[0]}_${params[1]}`;

      return {
        ...obj,
        [objKey]: [...(obj[objKey] || []), curr.value],
      };
    }, {});

    return API.patch('action-configs', {
      ...dataObj,
      ...defaultActions,
      templates: {
        ...dataObj.templates,
        ...templates,
      },
    })
      .then(() => {
        queryClient.invalidateQueries(buildClientEntity('multi-actions', client));
        showModal({
          type: 'success',
        });
      })
      .catch(res => {
        const response = createErrorFromResponse(res);
        // Process errors in order to match field name with response error
        const errors: any = {};
        Object.keys(response.errors).forEach(key => {
          if (key === 'approve') {
            const approveErrGroup = response.errors[key];
            Object.keys(approveErrGroup).forEach((approveErrorsKey: string) => {
              errors[`default-approve-${Number(approveErrorsKey) + 1}`] = approveErrGroup[approveErrorsKey];
            });
          }
          if (key === 'sign') {
            const signErrGroup = response.errors[key];
            Object.keys(signErrGroup).forEach((signErrorsKey: string) => {
              errors[`default-sign-${Number(signErrorsKey) + 1}`] = signErrGroup[signErrorsKey];
            });
          }
          if (key === 'templates') {
            const templatesErrGroup = response.errors[key];
            Object.keys(templatesErrGroup).forEach((templateErrorsKey: string) => {
              const slicedKey = templateErrorsKey.split('_');
              const action = slicedKey[0];
              const id = slicedKey[1];
              const fields = templatesErrGroup[templateErrorsKey];
              Object.keys(fields).forEach((fieldKey: string) => {
                errors[`${action}-${id}-${Number(fieldKey) + 1}`] = fields[fieldKey];
              });
            });
          }
        });
        setServerErrors(errors, methods.setError);
      });
  };

  const methods = useForm<(fieldName: any, error: { type: any; message: any }) => void>({
    defaultValues,
  });

  useEffect(() => {
    methods.reset(buildDefaultValues());
  }, [data]);

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      btnLabel={t('common.save')}
      isSubmitDisabled={!hasPermission}
      disabledBtnTooltip={t('common.noPermission')}
      hideCancelBtn
      showGlobalError
    >
      <DefaultActions
        data={data}
        methods={methods}
        hasPermission={hasPermission}
      />
      <ReactTableWrapper<ITemplateActions>
        data={tableData.items || []}
        columns={columns}
        totalCount={tableData.totalCount || 0}
        hideFilterBar
      />
    </Form>
  );
}

export default FormTable;

const NoActionHolder = styled.div`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.theme.colors.light};
  text-align: center;
  border-radius: ${props => props.theme.borderRadius};
  font-weight: bold;
`;
